// src/store/modules/logos.js
// State for the logo library: list, upload, rename, delete, bulk delete.

import api from '@/services/api'

export default {
  namespaced: true,

  state: () => ({
    logos:   [],
    loading: false,
    error:   null,
  }),

  getters: {
    logos:   (s) => s.logos,
    loading: (s) => s.loading,
    error:   (s) => s.error,
    byId:    (s) => (id) => s.logos.find(l => l.logoId === id) || null,
  },

  mutations: {
    SET_LOGOS(state, logos) { state.logos = logos },
    ADD_LOGO(state, logo)   { state.logos.unshift(logo) },
    UPDATE_LOGO(state, logo) {
      const idx = state.logos.findIndex(l => l.logoId === logo.logoId)
      if (idx >= 0) state.logos.splice(idx, 1, logo)
    },
    REMOVE_LOGO(state, logoId) {
      state.logos = state.logos.filter(l => l.logoId !== logoId)
    },
    REMOVE_LOGOS(state, logoIds) {
      const set = new Set(logoIds)
      state.logos = state.logos.filter(l => !set.has(l.logoId))
    },
    SET_LOADING(state, val) { state.loading = val },
    SET_ERROR(state, msg)   { state.error   = msg },
  },

  actions: {
    async fetchLogos({ commit }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await api.get('/designer/logos')
        commit('SET_LOGOS', data.logos)
        return data.logos
      } catch (err) {
        commit('SET_ERROR', err.response?.data?.error || 'Failed to load logos')
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Upload a logo file with optional metadata.
     * @param {File}   file
     * @param {Object} logoData  optional ZPL/Z64 metadata { z64, widthMm, heightMm, ... }
     */
    async uploadLogo({ commit }, { file, logoData }) {
      commit('SET_LOADING', true)
      try {
        const formData = new FormData()
        formData.append('file', file)
        if (logoData) formData.append('logoData', JSON.stringify(logoData))

        const { data } = await api.post('/designer/logos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        commit('ADD_LOGO', data.logo)
        return { ok: true, logo: data.logo }
      } catch (err) {
        const msg = err.response?.data?.error || 'Upload failed'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Rename a logo. Backend uses PUT for single-resource updates
     * per project convention.
     */
    async renameLogo({ commit }, { logoId, logoName }) {
      try {
        const { data } = await api.put(`/designer/logos/${logoId}`, { logoName })
        commit('UPDATE_LOGO', data.logo)
        return { ok: true, logo: data.logo }
      } catch (err) {
        const msg = err.response?.data?.error || 'Rename failed'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      }
    },

    /**
     * Delete a single logo. Backend also NULLs out any
     * designer_elements that reference this logoId — those
     * elements stay in their labels but render as empty
     * placeholders until reassigned.
     */
    async deleteLogo({ commit }, logoId) {
      try {
        await api.delete(`/designer/logos/${logoId}`)
        commit('REMOVE_LOGO', logoId)
        return { ok: true }
      } catch (err) {
        const msg = err.response?.data?.error || 'Delete failed'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      }
    },

    /**
     * Delete many logos at once. Backend route is POST
     * /api/designer/logos/bulk-delete (POST because DELETE-with-body
     * is awkward in axios). Returns { ok, deleted, missing }.
     */
    async bulkDeleteLogos({ commit }, logoIds) {
      try {
        const { data } = await api.post('/designer/logos/bulk-delete', { logoIds })
        // Only remove the IDs the backend actually confirmed as
        // deleted — keeps state consistent if some IDs were missing.
        commit('REMOVE_LOGOS', data.deletedIds || [])
        return {
          ok:      true,
          deleted: data.deleted || 0,
          missing: data.missing || 0,
        }
      } catch (err) {
        const msg = err.response?.data?.error || 'Bulk delete failed'
        commit('SET_ERROR', msg)
        return { ok: false, error: msg }
      }
    },
  },
}